import { useApplicationLocalStorage } from '../../../../application/application-hook';
import {
  Button,
  FormsCheckBox,
  FormsInputDropdown,
  MultiSelectDropdown,
  TimezoneMultiPicker,
} from '@platform/components';
import { AdminCohorts, CorporateClients, Partners, Countries, Industries } from '@platform/client';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import {
  BusinessChallengesEnum,
  OrganizationApplicationStatusEnum,
} from '@platform/client/schemas';

export type RepUserFilters = {
  timezone: string[] | null;
  applicationStatus: string[] | null;
  corporateClient: string[] | null;
  impactPartner: string[] | null;
  cohortMatches: string[] | null;
  hasPublished: string | null; // this will support explicit options of ['has published', 'has no published'] instead of a true/false which is ambiguous when unchecked
  matchStatus: string | null; // similar to hasPublished, ['has matched', 'has never matched']
  hasPublishedUnmatched: boolean | null;
  continents: string[] | null;
  industries: string[] | null;
  businessChallenges: string[] | null;
};

export const RepUserSearchForm = (props: {
  setFilters: React.Dispatch<React.SetStateAction<RepUserFilters | undefined>>;
}) => {
  const { updateApplicationData, getApplicationData, clearApplicationData } =
    useApplicationLocalStorage<RepUserFilters>('rep-search');
  const { handleSubmit, setValue, register, watch, control, reset } = useForm<RepUserFilters>({
    values: { ...getApplicationData() },
  });
  const onSubmit: SubmitHandler<RepUserFilters> = (data) => {
    updateApplicationData(data);
    props.setFilters(data);
  };

  const clearFilters = () => {
    reset({
      timezone: null,
      applicationStatus: null,
      corporateClient: null,
      impactPartner: null,
      cohortMatches: null,
      hasPublished: null,
      hasPublishedUnmatched: null,
      matchStatus: null,
      continents: null,
      industries: null,
    });
    clearApplicationData();
    props.setFilters(undefined);
  };

  const { data: cohortData } = AdminCohorts.useGetAllCohorts();
  const cohortOptions = useMemo(() => {
    return (cohortData?.cohorts || [])
      .map((cohort) => ({ label: cohort.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cohortData]);
  const { data: partnersData } = Partners.useGetActiveImpactPartners();
  const partnerOptions = useMemo(() => {
    return (partnersData || [])
      .map((partner) => ({ label: `${partner.name} [id: ${partner.id}]` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [partnersData]);
  const { data: clientsData } = CorporateClients.useGetAllCorporateClients();
  const clientOptions = useMemo(() => {
    return (clientsData || [])
      .map((client) => ({ label: `${client.name}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clientsData]);
  const { data: ContinentsData } = Countries.useGetContinents();
  const continentOptions = useMemo(() => {
    return (ContinentsData || [])
      .map((continent) => ({ label: continent.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [ContinentsData]);
  const { data: IndustriesData } = Industries.useGetIndustries();
  const industryOptions = useMemo(() => {
    return (IndustriesData || [])
      .map((industry) => ({ label: industry.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [IndustriesData]);

  return (
    <div className='min-w-full flex flex-col gap-4 items-center'>
      <h1>Representative User Search</h1>
      <form
        className='min-w-full md:min-w-1/2 flex flex-col gap-2'
        onSubmit={handleSubmit(onSubmit)}
      >
        <MultiSelectDropdown
          register={() => register('applicationStatus')}
          label='Application status'
          options={[
            ...Object.values(OrganizationApplicationStatusEnum).map((status) => ({
              label: status.replaceAll('_', ' '),
            })),
            { label: 'application incomplete' },
          ].sort((a, b) => a.label.localeCompare(b.label))}
          onSelectionChange={(items) => setValue('applicationStatus', items)}
          selectedItems={watch('applicationStatus') || []}
          placeholder='All'
          filterable={true}
        />
        <TimezoneMultiPicker
          placeholder='All'
          timezone={watch('timezone') ?? undefined}
          updateTimezone={(timezone) => setValue('timezone', timezone)}
          register={() => register('timezone')}
          filterable={true}
        />
        <MultiSelectDropdown
          placeholder='All'
          label='Continents'
          options={continentOptions}
          onSelectionChange={(items) => setValue('continents', items)}
          selectedItems={watch('continents') || []}
          register={() => register('continents')}
          filterable={true}
        />
        <MultiSelectDropdown
          placeholder='All'
          label='Corporate Client'
          options={[...(clientOptions || []), { label: 'None' }]}
          onSelectionChange={(items) => setValue('corporateClient', items)}
          selectedItems={watch('corporateClient') || []}
          register={() => register('corporateClient')}
          filterable={true}
        />
        <MultiSelectDropdown
          placeholder='All'
          label='Business Challenges'
          options={Object.values(BusinessChallengesEnum).map((challenge) => ({
            label: challenge,
          }))}
          onSelectionChange={(items) => setValue('businessChallenges', items)}
          selectedItems={watch('businessChallenges') || []}
          register={() => register('businessChallenges')}
          filterable={true}
        />
        <MultiSelectDropdown
          placeholder='All'
          label='Industries'
          options={[...(industryOptions || []), { label: 'None' }]}
          onSelectionChange={(items) => setValue('industries', items)}
          selectedItems={watch('industries') || []}
          register={() => register('industries')}
          filterable={true}
        />
        <MultiSelectDropdown
          placeholder='All'
          label='Impact partner'
          options={partnerOptions}
          onSelectionChange={(items) => setValue('impactPartner', items)}
          selectedItems={watch('impactPartner') || []}
          register={() => register('impactPartner')}
          filterable={true}
        />
        <FormsInputDropdown
          type='default'
          name='matchStatus'
          placeholder='All'
          label='Has matched'
          items={[
            { text: 'has matched', textValue: 'has_matched' },
            { text: 'has never matched', textValue: 'has_never_matched' },
          ]}
          control={control}
        />
        <MultiSelectDropdown
          placeholder='All'
          label='Cohort matches'
          options={cohortOptions}
          onSelectionChange={(items) => setValue('cohortMatches', items)}
          selectedItems={watch('cohortMatches') || []}
          register={() => register('cohortMatches')}
          filterable={true}
        />
        <FormsInputDropdown
          type='default'
          name='hasPublished'
          placeholder='All'
          label='Has published project'
          items={[
            { text: 'has published project(s)', textValue: 'has_published' },
            { text: 'has no published project(s)', textValue: 'has_no_published' },
          ]}
          control={control}
        />
        <FormsCheckBox
          size='sm'
          name='hasPublishedUnmatched'
          control={control}
          aria-label='Has Published Unmatched'
          description='Has a published project that is not matched'
        />
      </form>
      <div className='flex justify-between gap-8 items-center mx-2'>
        <Button
          size='sm'
          variant='tertiary-danger'
          onClick={() => clearFilters()}
          additionalClassnames='w-fit'
        >
          clear filters
        </Button>
        <Button
          size='sm'
          variant='primary'
          onClick={handleSubmit(onSubmit)}
          additionalClassnames='w-fit'
        >
          Preview list
        </Button>
      </div>
    </div>
  );
};
